import { PROTOCOL_VERSION, type SessionEnvelope, isSessionEnvelope } from "@bianhua/protocol";
import type { RuntimeConfig } from "./config";
import { getClientPlatform, type ClientPlatform } from "./platform";

export type GatewayState = "offline" | "connecting" | "ready" | "syncing" | "reconnecting" | "error";

export type RpcOptions = {
  timeoutMs?: number;
  /**
   * A stable caller-owned identity for a mutation. Reusing it with the same
   * method and params retrieves the gateway's durable result instead of
   * executing the mutation again after a reconnect or window hand-off.
   */
  requestId?: string;
};

type PendingRequest = {
  resolve(value: unknown): void;
  reject(reason: unknown): void;
  timeout: number;
  envelope: SessionEnvelope;
};

const CURSOR_KEY = "bianhua-session-cursor:";
const SESSION_KEY = "bianhua-session-id:";
const PRINCIPAL_KEY = "bianhua-principal-id:";
const CLIENT_VERSION = typeof __BIANHUA_APP_VERSION__ === "string" ? __BIANHUA_APP_VERSION__ : "0.1.0";

const LEGACY_ELICITATION_CATEGORIES: Record<string, string> = {
  "item/commandExecution/requestApproval": "approval",
  "item/fileChange/requestApproval": "approval",
  "item/permissions/requestApproval": "approval",
  applyPatchApproval: "approval",
  execCommandApproval: "approval",
  "mcpServer/elicitation/request": "mcp_elicitation",
  "item/tool/requestUserInput": "user_input",
  "item/tool/call": "tool_call",
  "account/chatgptAuthTokens/refresh": "auth_refresh",
  "attestation/generate": "attestation",
};

function normalizeLegacyElicitation(input: unknown): { value: unknown; upgraded: boolean } {
  if (!input || typeof input !== "object" || Array.isArray(input)) return { value: input, upgraded: false };
  const record = input as Record<string, unknown>;
  if (record.kind !== "elicitation.request") return { value: input, upgraded: false };
  const payload = record.payload;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return { value: input, upgraded: false };
  const legacy = payload as Record<string, unknown>;
  const method = typeof legacy.method === "string" ? legacy.method : "";
  const category = LEGACY_ELICITATION_CATEGORIES[method];
  if (!category) return { value: input, upgraded: false };
  if (legacy.source !== undefined && legacy.source !== "codex_app_server") return { value: input, upgraded: false };
  if (legacy.category !== undefined && legacy.category !== category) return { value: input, upgraded: false };
  const upgraded = legacy.source === undefined || legacy.category === undefined;
  if (!upgraded) return { value: input, upgraded: false };
  return {
    value: {
      ...record,
      payload: {
        ...legacy,
        source: "codex_app_server",
        category,
      },
    },
    upgraded: true,
  };
}

function normalizeLegacyRpcError(input: unknown): unknown {
  if (!input || typeof input !== "object" || Array.isArray(input)) return input;
  const envelope = input as Record<string, unknown>;
  if (envelope.kind !== "rpc.response") return input;
  const payload = envelope.payload;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return input;
  const error = (payload as Record<string, unknown>).error;
  if (!error || typeof error !== "object" || Array.isArray(error)) return input;
  const errorRecord = error as Record<string, unknown>;
  const upstreamCode = errorRecord.code;
  const numericCode = typeof upstreamCode === "number" && Number.isSafeInteger(upstreamCode)
    ? upstreamCode
    : typeof upstreamCode === "string" && /^-\d+$/.test(upstreamCode)
      ? Number(upstreamCode)
      : null;
  if (numericCode === null || !Number.isSafeInteger(numericCode)) return input;

  const existingDetails = errorRecord.details;
  const details = existingDetails && typeof existingDetails === "object" && !Array.isArray(existingDetails)
    ? { ...(existingDetails as Record<string, unknown>), upstreamCode }
    : existingDetails === undefined
      ? { upstreamCode }
      : { upstreamCode, upstreamDetails: existingDetails };
  return {
    ...envelope,
    payload: {
      ...(payload as Record<string, unknown>),
      error: {
        ...errorRecord,
        code: `CODEX_JSON_RPC_ERROR_${Math.abs(numericCode)}`,
        details,
      },
    },
  };
}

function id(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export class GatewayClient extends EventTarget {
  readonly config: RuntimeConfig;
  private socket: WebSocket | null = null;
  private pending = new Map<string, PendingRequest>();
  private reconnectAttempt = 0;
  private reconnectTimer = 0;
  private heartbeatTimer = 0;
  private manualClose = false;
  private lastCursor = 0;
  private committedCursor = 0;
  private heldCursors = new Map<string, number>();
  private pendingElicitationResponses = new Map<string, SessionEnvelope>();
  private sessionId: string | null;
  private principalId: string | null;
  private connectionId: string | null = null;
  private previousConnectionId: string | null = null;
  private heartbeatIntervalMs = 20_000;
  private maxMessageBytes = 1_048_576;
  private lastInboundAt = 0;
  private resumeInFlight = false;
  private readonly storageScope: string;
  private readonly cursorStorage: Storage;
  private readonly clientPlatform: ClientPlatform;
  private readonly resumeRequestListener = () => this.resumeTransport();
  state: GatewayState = "offline";

  constructor(config: RuntimeConfig) {
    super();
    this.config = config;
    const endpoint = config.gatewayUrl ? new URL(config.gatewayUrl).host : "unconfigured";
    this.storageScope = `${config.projectId}:${endpoint}`;
    // A durable gateway session is shared by all windows, but a cursor is a
    // per-connection delivery checkpoint. Sharing it through localStorage
    // lets one window skip events that only another window observed.
    this.cursorStorage = typeof sessionStorage === "undefined" ? localStorage : sessionStorage;
    this.clientPlatform = getClientPlatform();
    this.committedCursor = Number(this.cursorStorage.getItem(`${CURSOR_KEY}${this.storageScope}`)) || 0;
    this.lastCursor = this.committedCursor;
    this.sessionId = localStorage.getItem(`${SESSION_KEY}${this.storageScope}`);
    this.principalId = localStorage.getItem(`${PRINCIPAL_KEY}${this.storageScope}`);
    window.addEventListener?.("bianhua:gateway-resume-request", this.resumeRequestListener);
  }

  /**
   * Explicit foreground-resume hook used by mobile shells. It never creates a
   * second socket while one is opening and never overrides an intentional
   * manual close. An open socket receives an immediate heartbeat so a stale
   * transport is detected without waiting for the periodic timer.
   */
  resumeTransport(): void {
    if (!this.config.gatewayUrl || this.manualClose) return;
    const readyState = this.socket?.readyState;
    if (readyState === WebSocket.OPEN) {
      try {
        this.send(this.envelope("connection.heartbeat", {
          nonce: crypto.randomUUID(),
          direction: "ping",
          lastReceivedCursor: this.committedCursor,
          reason: "client-resumed",
        }));
      } catch {
        this.socket?.close(4000, "resume heartbeat failed");
      }
      return;
    }
    if (readyState === WebSocket.CONNECTING || readyState === 2) return;
    this.socket = null;
    this.connect();
  }

  connect(): void {
    if (!this.config.gatewayUrl || this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return;
    if (this.config.authMode === "ticket" && this.config.authToken && !/^[A-Za-z0-9_-]{16,512}$/.test(this.config.authToken)) {
      this.setState("error");
      throw new Error("桌面连接票据格式无效");
    }
    this.manualClose = false;
    this.setState(this.reconnectAttempt ? "reconnecting" : "connecting");
    const protocols = this.config.authMode === "ticket" && this.config.authToken
      ? [PROTOCOL_VERSION, `bianhua.ticket.${this.config.authToken}`]
      : [PROTOCOL_VERSION];
    this.socket = new WebSocket(this.config.gatewayUrl, protocols);
    this.socket.addEventListener("open", () => this.onOpen());
    this.socket.addEventListener("message", (event) => this.onMessage(event));
    this.socket.addEventListener("close", (event) => this.onClose(event));
    this.socket.addEventListener("error", () => this.dispatchEvent(new CustomEvent("transport-error", { detail: { code: "SOCKET_ERROR" } })));
  }

  close(): void {
    this.manualClose = true;
    window.clearTimeout(this.reconnectTimer);
    window.clearInterval(this.heartbeatTimer);
    this.socket?.close(1000, "client close");
    this.socket = null;
    this.setState("offline");
    this.rejectPending("连接已关闭");
    this.heldCursors.clear();
    this.pendingElicitationResponses.clear();
    this.lastCursor = this.committedCursor;
  }

  async rpc<T = unknown>(method: string, params: unknown, options: RpcOptions = {}): Promise<T> {
    if (this.state !== "ready" || this.socket?.readyState !== WebSocket.OPEN) throw new Error("服务器未连接");
    const stableRequestId = options.requestId?.trim();
    if (stableRequestId && !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(stableRequestId)) {
      throw new Error("请求恢复标识无效");
    }
    const envelope = this.envelope("rpc.request", { method, params }, undefined, stableRequestId);
    const requestId = envelope.id;
    if (this.pending.has(requestId)) {
      throw Object.assign(new Error(`请求仍在等待服务器确认：${requestId}`), {
        code: "REQUEST_IN_FLIGHT",
        retryable: true,
      });
    }
    const promise = new Promise<T>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error(`服务器请求超时：${method}`));
      }, options.timeoutMs || this.config.requestTimeoutMs);
      this.pending.set(requestId, { resolve: resolve as (value: unknown) => void, reject, timeout, envelope });
    });
    try {
      this.send(envelope);
    } catch (error) {
      const pending = this.pending.get(requestId);
      if (pending) window.clearTimeout(pending.timeout);
      this.pending.delete(requestId);
      throw error;
    }
    return promise;
  }

  respond(
    requestId: string,
    response: {
      outcome: "accepted" | "declined" | "cancelled" | "expired" | "error";
      result?: unknown;
      error?: { code: string; message: string; retryable: boolean };
    },
  ): void {
    const payload = response.outcome === "error"
      ? { outcome: response.outcome, error: response.error || { code: "CLIENT_RESPONSE_ERROR", message: "客户端无法处理服务器请求", retryable: false } }
      : { outcome: response.outcome, result: response.result };
    const envelope = this.envelope("elicitation.response", payload, requestId);
    this.pendingElicitationResponses.set(requestId, envelope);
    this.send(envelope);
  }

  acknowledgeElicitation(requestId: string): void {
    this.pendingElicitationResponses.delete(requestId);
    this.heldCursors.delete(requestId);
    this.commitCursor();
  }

  private envelope(kind: string, payload: unknown, replyTo?: string, requestId?: string): SessionEnvelope {
    const envelope = {
      protocol: PROTOCOL_VERSION,
      id: requestId || id("msg"),
      kind,
      sentAt: new Date().toISOString(),
      payload,
    } as SessionEnvelope;
    if (replyTo) envelope.replyTo = replyTo;
    return envelope;
  }

  private send(envelope: SessionEnvelope): void {
    if (this.socket?.readyState !== WebSocket.OPEN) throw new Error("服务器未连接");
    const encoded = JSON.stringify(envelope);
    if (new TextEncoder().encode(encoded).byteLength > this.maxMessageBytes) throw new Error("会话消息超过服务器允许的大小");
    this.socket.send(encoded);
  }

  private onOpen(): void {
    const hello: Record<string, unknown> = {
      client: {
        clientId: this.config.clientId,
        name: "彼岸花·智体",
        version: CLIENT_VERSION,
        platform: this.clientPlatform,
      },
      supportedProtocols: [PROTOCOL_VERSION],
    };
    if (this.sessionId) hello.lastSessionId = this.sessionId;
    this.send(this.envelope("connection.hello", hello));
  }

  private startHeartbeat(): void {
    window.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = window.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        if (this.lastInboundAt && Date.now() - this.lastInboundAt > this.heartbeatIntervalMs * 3) {
          this.socket.close(4000, "heartbeat timeout");
          return;
        }
        this.send(this.envelope("connection.heartbeat", {
          nonce: crypto.randomUUID(),
          direction: "ping",
          lastReceivedCursor: this.committedCursor,
        }));
      }
    }, this.heartbeatIntervalMs);
  }

  private requestResume(): boolean {
    if (!this.sessionId || this.resumeInFlight) return false;
    const payload: Record<string, unknown> = {
      sessionId: this.sessionId,
      clientId: this.config.clientId,
      afterCursor: this.committedCursor,
    };
    if (this.previousConnectionId) payload.previousConnectionId = this.previousConnectionId;
    this.resumeInFlight = true;
    try {
      this.send(this.envelope("connection.resume", payload));
      return true;
    } catch (error) {
      this.resumeInFlight = false;
      throw error;
    }
  }

  private resetCursor(reason: string, baseline = 0): void {
    const safeBaseline = Number.isSafeInteger(baseline) && baseline >= 0 ? baseline : 0;
    this.lastCursor = safeBaseline;
    this.committedCursor = safeBaseline;
    this.heldCursors.clear();
    this.pendingElicitationResponses.clear();
    this.resumeInFlight = false;
    if (safeBaseline) this.cursorStorage.setItem(`${CURSOR_KEY}${this.storageScope}`, String(safeBaseline));
    else this.cursorStorage.removeItem(`${CURSOR_KEY}${this.storageScope}`);
    this.dispatchEvent(new CustomEvent("reset-required", { detail: { reason } }));
  }

  private rejectPending(message: string): void {
    for (const [requestId, pending] of this.pending) {
      window.clearTimeout(pending.timeout);
      pending.reject(new Error(`${message}：${requestId}`));
    }
    this.pending.clear();
  }

  private markReady(): void {
    this.reconnectAttempt = 0;
    this.setState("ready");
    for (const pending of this.pending.values()) this.send(pending.envelope);
    for (const response of this.pendingElicitationResponses.values()) this.send(response);
  }

  private commitCursor(): void {
    const blockedAt = this.heldCursors.size ? Math.min(...this.heldCursors.values()) : undefined;
    const next = blockedAt === undefined ? this.lastCursor : Math.min(this.lastCursor, blockedAt - 1);
    if (next <= this.committedCursor) return;
    this.committedCursor = next;
    this.cursorStorage.setItem(`${CURSOR_KEY}${this.storageScope}`, String(next));
  }

  private onMessage(event: MessageEvent): void {
    if (typeof event.data !== "string") {
      this.socket?.close(1003, "text frames required");
      this.dispatchEvent(new CustomEvent("protocol-error", { detail: { code: "BINARY_FRAME_REFUSED" } }));
      return;
    }
    if (new TextEncoder().encode(event.data).byteLength > this.maxMessageBytes) {
      this.socket?.close(1009, "message too large");
      this.dispatchEvent(new CustomEvent("protocol-error", { detail: { code: "MESSAGE_TOO_LARGE" } }));
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(String(event.data));
    } catch {
      this.socket?.close(1007, "invalid json");
      this.dispatchEvent(new CustomEvent("protocol-error", { detail: { code: "INVALID_JSON" } }));
      return;
    }
    const rpcNormalized = normalizeLegacyRpcError(parsed);
    const normalized = normalizeLegacyElicitation(rpcNormalized);
    const cursorlessLegacyElicitation = normalized.upgraded
      && normalized.value !== null
      && typeof normalized.value === "object"
      && !Object.hasOwn(normalized.value, "cursor")
      && isSessionEnvelope({ ...(normalized.value as Record<string, unknown>), cursor: 1 });
    if (!isSessionEnvelope(normalized.value) && !cursorlessLegacyElicitation) {
      this.socket?.close(1002, "invalid envelope");
      this.dispatchEvent(new CustomEvent("protocol-error", { detail: { code: "INVALID_ENVELOPE", parsed: normalized.value } }));
      return;
    }
    this.lastInboundAt = Date.now();
    // 0.1.34/0.1.35 gateways omitted source/category and used a cursor-free
    // copy only for current pending approval state. Keep that narrowly scoped
    // compatibility path until every deployed gateway has been upgraded.
    const envelope = normalized.value as SessionEnvelope;
    const payload = envelope.payload as Record<string, unknown> | null;

    if (envelope.kind === "connection.welcome" && payload) {
      const previousSessionId = this.sessionId;
      const previousPrincipalId = this.principalId;
      const nextPrincipalId = typeof payload.principalId === "string" && payload.principalId.length >= 16
        ? payload.principalId
        : null;
      this.connectionId = String(payload.connectionId || "");
      this.sessionId = String(payload.sessionId || "");
      this.principalId = nextPrincipalId;
      this.heartbeatIntervalMs = Math.max(5_000, Math.min(120_000, Number(payload.heartbeatIntervalMs) || 20_000));
      this.maxMessageBytes = Math.max(65_536, Math.min(16 * 1024 * 1024, Number(payload.maxMessageBytes) || 1_048_576));
      if (this.sessionId) localStorage.setItem(`${SESSION_KEY}${this.storageScope}`, this.sessionId);
      if (this.principalId) localStorage.setItem(`${PRINCIPAL_KEY}${this.storageScope}`, this.principalId);
      else localStorage.removeItem(`${PRINCIPAL_KEY}${this.storageScope}`);
      const principalChanged = Boolean(previousPrincipalId && previousPrincipalId !== nextPrincipalId);
      if (principalChanged) {
        this.resetCursor("登录账户已更换，旧会话游标已经失效");
        this.rejectPending("登录账户已更换，旧账户请求不会在新账户中重放");
        this.heldCursors.clear();
        this.pendingElicitationResponses.clear();
      }
      if (previousSessionId && this.sessionId && previousSessionId !== this.sessionId) {
        this.resetCursor("服务器建立了新的会话，旧游标已经失效");
        if (!principalChanged) this.rejectPending("服务器会话已更换，请重新提交请求");
      }
      this.startHeartbeat();
      // A first-time client can join a server-side session that already has
      // durable events (for example after another tab or an earlier smoke
      // test). Always resume from the committed cursor when the server
      // supports it; otherwise the first live event can look like a gap.
      const canResume = Boolean(this.sessionId && payload.resumeSupported !== false);
      if (canResume) this.requestResume();
      else {
        if (previousSessionId && payload.resumeSupported === false) this.resetCursor("服务器不支持断线续传，已重新同步当前会话");
        this.markReady();
      }
    }
    if (envelope.kind === "connection.resumed" && payload) {
      this.resumeInFlight = false;
      if (payload.resetRequired === true) {
        this.resetCursor(
          String(payload.reason || "服务器要求重新同步会话"),
          Number(payload.replayedThroughCursor),
        );
        this.rejectPending("会话无法续传，请重新提交请求");
      }
      this.markReady();
    }
    if (envelope.kind === "connection.heartbeat" && payload?.direction === "ping") {
      this.send(this.envelope("connection.heartbeat", {
        nonce: String(payload.nonce),
        direction: "pong",
        lastReceivedCursor: this.committedCursor,
      }));
    }
    if (envelope.kind === "connection.heartbeat" && payload?.direction === "pong" && this.state === "ready") {
      // Heartbeats also reconcile current server state. A live approval
      // envelope may be the final event before a turn blocks, so there may be
      // no later cursor gap to reveal that it was missed. Resuming from the
      // committed cursor lets the server replay/re-advertise that approval
      // without tearing down a healthy socket.
      this.requestResume();
    }

    const cursor = typeof envelope.cursor === "number" ? envelope.cursor : undefined;
    if (cursor !== undefined) {
      const repeatedElicitation = cursor <= this.lastCursor && envelope.kind === "elicitation.request";
      if (cursor <= this.lastCursor && !repeatedElicitation) return;
      if (cursor > this.lastCursor + 1) {
        const startedResume = this.requestResume();
        if (startedResume) {
          // The WebSocket is still open. Only the durable event stream is
          // catching up, so do not present this as a physical reconnect or
          // trigger the full connection-ready recovery workflow again.
          this.setState("syncing");
          this.dispatchEvent(new CustomEvent("cursor-gap", { detail: { expected: this.lastCursor + 1, received: cursor } }));
        }
        return;
      }
      if (!repeatedElicitation) {
        this.lastCursor = cursor;
        if (envelope.kind === "elicitation.request") {
          const requestId = String(payload?.requestId || "");
          this.heldCursors.set(requestId, cursor);
        }
        this.commitCursor();
      }
    }

    const elicitationRequestId = envelope.kind === "elicitation.request" ? String(payload?.requestId || "") : "";
    if (elicitationRequestId && this.pendingElicitationResponses.has(elicitationRequestId)) {
      this.send(this.pendingElicitationResponses.get(elicitationRequestId)!);
      return;
    }

    if (envelope.kind === "rpc.response" && payload) {
      const requestId = String(envelope.replyTo || "");
      const pending = this.pending.get(requestId);
      if (pending) {
        window.clearTimeout(pending.timeout);
        this.pending.delete(requestId);
        if (payload.error) {
          const errorPayload = payload.error as Record<string, unknown>;
          const error = new Error(String(errorPayload.message || "服务器请求失败"));
          Object.assign(error, { code: errorPayload.code, retryable: errorPayload.retryable, details: errorPayload.details });
          pending.reject(error);
        }
        else pending.resolve(payload.result);
      }
    }
    this.dispatchEvent(new CustomEvent("envelope", { detail: envelope }));
    this.dispatchEvent(new CustomEvent(envelope.kind, { detail: envelope }));
  }

  private onClose(event: CloseEvent): void {
    window.clearInterval(this.heartbeatTimer);
    this.previousConnectionId = this.connectionId;
    this.connectionId = null;
    this.socket = null;
    this.resumeInFlight = false;
    if (this.manualClose) return;
    if (event.code === 4001 || event.code === 4003) {
      this.setState("error");
      this.rejectPending("服务器身份验证失败");
      this.dispatchEvent(new CustomEvent("auth-error", { detail: { code: event.code } }));
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.reconnectAttempt += 1;
    this.setState("reconnecting");
    const delay = Math.min(30_000, 750 * 2 ** Math.min(this.reconnectAttempt, 6)) + Math.random() * 400;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private setState(state: GatewayState): void {
    if (this.state === state) return;
    const previousState = this.state;
    this.state = state;
    this.dispatchEvent(new CustomEvent("state", { detail: { state, previousState } }));
  }
}
